import { Component, Element, Prop, h, Host, Event, EventEmitter, State, Listen, Watch, Method } from '@stencil/core';
import { generateId, focusElement, isDescendant, isInError, getRequiredInd, ariaDescribedby } from '../../utils/functions';
import { getInternalFormat, getInternalFormatFromDate, isDateString, formatDate } from '../../utils/dates';
import { breakpoints, KEY_CODES } from '../../utils/variables';

/**
 * @slot label - Label for the date picker input. Alternatively, you can use the label attribute.
 */
@Component({
  tag: 'som-date-picker-input',
  styleUrl: 'som-date-picker-input.scss',
})
export class SomDatePickerInput {
  @Element() el: HTMLElement;

  resizeTimeout;

  updateResponsivenessCb;

  inputEl: HTMLInputElement;

  popoverEl: HTMLSomPopoverElement;

  datePickerMobileEl: HTMLSomDatePickerMobileElement;

  datePickerEl: HTMLSomDatePickerElement;

  popoverId = generateId('som-date-picker-popover');

  helpElementId: string;

  tooltipID: string = generateId('som-tooltipID-');

  enableInputFocus = true;

  // Debounce timeout used to prevent flickering when focus is on input and user focuses out of the browser body content (ex.: URL bar) and clicking back inside the page.
  focusBlurDebounceTimeout;

  /**
   * This property sets the input label.
   */
  @Prop() label = '';

  /**
   * This property is a list of CSS classes that can be used to style the label.  
   * @deprecated with version 4.0.1 release
   */
  @Prop() labelClass: string = '';

   /**
   * This property will visually hide the label.  A label is still required to be provided for screenreader support.
   */
   @Prop() hideLabel: boolean = false;     

  /**
   * This property sets the help text rendered below input.
   */
  @Prop() help = '';

  /**
   * This property sets the input error message. If a non-empty string is given, it will be used as the error message.
   */
  @Prop({ mutable: true, reflect: true }) error: string | null = null;

  /**
   * This boolean sets the input to required.
   */
  @Prop() required = false;

  /**
  * This property is used to override the required indicator. By default, forms elements will show an `optional` indicator
  * but some forms are better served showing the `required` indicator instead.
  */
  @Prop() requiredInd: 'required' | 'optional' | 'none';    

  /**
   * This property sets the input placeholder text.
   */
  @Prop() placeholder = '';

  /**
   * When set to `true`, this boolean ensures that the input will be disabled.
   */
  @Prop() disabled = false;

  /**
   * When set to `true`, this boolean ensures that the input will be read only.
   */
  @Prop() readonly = false;

  /**
   * This property sets the ARIA label associated with this input.
   */
  @Prop() inputAriaLabel = '';

  /**
   * This property sets the input name, used when submitting the form.
   */
  @Prop() name = '';

  /**
   * This property sets the value of the input.
   */
  @Prop({ mutable: true, reflect: true }) value = '';
 
  @Watch('value')
  handleValueUpdate(newVal) {
    if (newVal && this.inputEl) {
      this.inputEl.value = newVal;
      this.date = isDateString(this.value, this.format) ? getInternalFormat(this.value, this.format) : '';
    }
  }

  /**
   * This property sets the horizontal position of the date picker popover.
   */
  @Prop() horizontalPosition: 'left' | 'right' = 'left';

  /**
   * This property sets the vertical position of the date picker popover.
   */
  @Prop() verticalPosition: 'top' | 'bottom' = 'bottom';

  /**
   * This property sets the date format.
   */
  @Prop() format: 'MM-DD-YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD' | 'YYYY/MM/DD' = 'MM/DD/YYYY';

  /**
   * This property can be used to specifically set an ID to the input field. If an ID is not provided, it will be dynamically generated.
   */
  @Prop() inputId: string = generateId('som-date-picker-input');  

  /**
   * This property is used to set the tooltip text.
   */
  @Prop() tooltip: string | null = null

  /**
   * This boolean displays only one month at a time.
   */
   @Prop() singleMonthView: boolean = false;  

  @State() date = '';

  @State() isMobile = false;

  /**
   * This event is fired whenever a new date is selected.
   * @Returns `event.detail` will contain the current selected date.
   */
  @Event() dateChanged: EventEmitter;

  @Listen('blur', { target: 'window', capture: true })
  handleWindowBlur(e) {
    if (this.inputEl === document.activeElement) {
      // Focus is removed from the page (i.e. user views another browser tab). We don't want to trigger an input focus.
      e.stopPropagation();
      e.preventDefault();
    }
  }

  @Listen('focus', { target: 'window', capture: true })
  handleWindowFocus(e) {
    if (this.inputEl === document.activeElement) {
      // Focus is given to the page (i.e. user comes back from another browser tab). We don't want to trigger an input focus.
      e.stopPropagation();
      e.preventDefault();
    }
  }

  @Listen('resize', { target: 'window' })
  handleWindowResize() {
    clearTimeout(this.resizeTimeout);

    // Setup debounce so we're not constantly reading screen width
    this.resizeTimeout = setTimeout(this.updateResponsivenessCb, 300);
  }

  @Listen('keydown')
  async handleKeyDown(e) {
    if (e.keyCode === KEY_CODES.TAB) {
      const hasFocus = isDescendant(this.popoverEl, document.activeElement);
      if (!hasFocus) {
        this.hideDatePicker();
      }
    } else if (!this.readonly) {
      if (e.keyCode === KEY_CODES.ESC) {
        // For both mobile and desktop
        e.preventDefault();
        this.focusSilently();
      } else if (e.keyCode === KEY_CODES.ARROW_DOWN) {
        // Execute this code if calendar is not already shown
        e.preventDefault();

        this.showDatePicker(true);

        if (this.datePickerEl) {
          this.datePickerEl.setFocus();
        }
      }
    }
  }


  /**
   * This method resets the date picker value to null.
   */
  @Method()
  async resetValue() {
    this.inputEl.value = '';
    this.date = '';
  }

  componentWillLoad() {
    this.helpElementId = `${this.inputId}-help`;
    
    this.updateResponsivenessCb = this.updateResponsiveness.bind(this);

    this.updateResponsiveness();

    if (this.value) {
      this.date = isDateString(this.value, this.format) ? getInternalFormat(this.value, this.format) : '';
    }
  }

  componentDidLoad() {
    if (this.value) {
      this.inputEl.value = this.value;
    }
  }

  updateResponsiveness() {
    this.isMobile = window.innerWidth < breakpoints.md;
  }

  async focusSilently() {
    // We put focus on the input
    this.enableInputFocus = false;
    await focusElement(this.inputEl);

    // For IE -- Put the re-enable on the top of the stack. This prevents the 'focus' event to be triggered right after the date picker is closed (which would reopen it right away).
    setTimeout(() => {
      this.enableInputFocus = true;
    }, 0);
  }

  handleInputFocus() {
    if (this.enableInputFocus) {
      clearTimeout(this.focusBlurDebounceTimeout);
      this.focusBlurDebounceTimeout = setTimeout(() => {
        this.showDatePicker();
      }, 100);
    }
  }

  handleInputBlur() {
    if (this.popoverEl) {
      clearTimeout(this.focusBlurDebounceTimeout);
      this.focusBlurDebounceTimeout = setTimeout(() => {
        const hasFocus = isDescendant(this.popoverEl, document.activeElement);
        if (!hasFocus) {
          this.hideDatePicker();
        }
      }, 200);
    }
  }

  handleInputClick() {
    this.showDatePicker();
  }

  async handleInputText(e) {
    this.focusSilently();
    this.hideDatePicker();

    this.value = e.target.value;
    this.date = isDateString(this.value, this.format) ? getInternalFormat(this.value, this.format) : '';

    this.dateChanged.emit(this.value);
  }

  async handleDateChange(e) {
    this.error = null;
    this.focusSilently();
    this.hideDatePicker();

    this.value = formatDate(e.detail, this.format);
    this.date = e.detail ? getInternalFormatFromDate(e.detail) : '';

    this.inputEl.value = this.value;
    this.dateChanged.emit(this.value);
  }

  handleMobileDateChange(e) {
    this.focusSilently();
    this.hideDatePicker();

    const { value1, date1 } = e.detail;
    this.value = value1;
    this.date = date1 ? getInternalFormatFromDate(date1) : '';

    this.inputEl.value = this.value;
    this.dateChanged.emit(this.value);
  }

  isDatePickerDisplayed() {
    if (this.popoverEl) {
      if (this.popoverEl.hasAttribute('show') && this.popoverEl.getAttribute('show') !== 'false') {
        return true;
      }
    } else if (this.datePickerMobileEl) {
      if (this.datePickerMobileEl.hasAttribute('show') && this.datePickerMobileEl.getAttribute('show') !== 'false') {
        return true;
      }
    }

    return false;
  }

  async showDatePicker(focusOnShow = false) {
    if (!this.isDatePickerDisplayed()) {
      // Wait for date picker to reset before showing it
      await this.resetDatePicker();

      if (this.datePickerMobileEl) {
        this.datePickerMobileEl.setAttribute('focus-input-on-show', focusOnShow ? 'true' : 'false');
        this.datePickerMobileEl.setAttribute('show', 'true');
      } else {
        this.popoverEl.setAttribute('show', 'true');
      }
    }
  }

  async resetDatePicker() {
    if (this.datePickerMobileEl) {
      this.datePickerMobileEl.setAttribute('date1', this.date);
      this.datePickerMobileEl.setAttribute('value1', this.value);
    } else if (this.datePickerEl) {
      this.datePickerEl.setAttribute('date', this.date);
      this.datePickerEl.setAttribute('value', this.value);
    }

    const datePickerEl = this.datePickerMobileEl || this.datePickerEl;
    return datePickerEl.reset();
  }

  hideDatePicker() {
    const el = this.datePickerMobileEl || this.popoverEl;
    el.removeAttribute('show');
  }

  handlePopoverClose() {}

  handleMobileClose() {
    this.focusSilently();
  }

  render() {
    const _isInError = isInError(this.error);
    const ariaInvalid = _isInError ? { 'aria-invalid': '' } : null;
    const ariaRequired = _isInError ? { 'aria-required': '' } : null;
    const ariaReadonly = this.readonly ? { 'aria-readonly': '' } : null;
    const ariaDisabled = this.disabled ? { 'aria-disabled': '' } : null;
    const helpMessage = _isInError ? this.error : this.help;
    const _requiredInd = getRequiredInd(h, this.required, this.requiredInd);

    return (
      <Host tabindex="-1">
        <div class="form-group">
          <div>
            <label class={`${this.labelClass} ${this.hideLabel ? 'sr-only' : ''}`} htmlFor={this.inputId}>
              <slot name="label">{this.label}</slot>
              {_requiredInd}
            </label>
            { this.tooltip ?
              <span>
                <som-button class='tooltip-button' button-aria-label={`${this.label} information`} button-type="clear" icon-position="only" color="primary" icon="information" aria-describedby={this.tooltipID}>
                </som-button>
                <som-tooltip tooltip-id={this.tooltipID}>
                  {this.tooltip}
                </som-tooltip>
              </span> : null
            }
          </div>

          <div slot="content">
            <input
              id={this.inputId}
              ref={ref => {
                this.inputEl = ref;
              }}
              type="text"
              class={`som-dpi-input form-control ${_isInError ? 'is-invalid' : ''}`}
              placeholder={this.placeholder || null}
              name={this.name || null}
              disabled={this.disabled}
              readonly={this.readonly}
              required={this.required}
              aria-label={this.inputAriaLabel || null}
              onClick={this.readonly ? null : this.handleInputClick.bind(this)}
              onFocus={this.readonly ? null : this.handleInputFocus.bind(this)}
              onBlur={this.readonly ? null : this.handleInputBlur.bind(this)}
              onInput={this.readonly ? null : this.handleInputText.bind(this)}
              {...ariaDescribedby(this.help, this.error, this.helpElementId)}
              {...ariaDisabled}
              {...ariaReadonly}
              {...ariaInvalid}
              {...ariaRequired}
            />
          </div>
          
          {helpMessage ? (
          <small id={this.helpElementId} class={`form-text ${_isInError ? 'text-error' : 'text-muted'}`}>
            {helpMessage}
          </small>
        ) : null}
        </div>
        {this.isMobile ? (
          <som-date-picker-mobile
            ref={ref => {
              this.datePickerMobileEl = ref;
            }}
            date1={this.date} // Expects MM/DD/YYY format
            value1={this.value}
            format={this.format}
            onClose={this.handleMobileClose.bind(this)}
            onDatesChanged={this.handleMobileDateChange.bind(this)}
          />
        ) : (
          <som-popover
            ref={ref => {
              this.popoverEl = ref;
            }}
            id={this.popoverId}
            for={this.inputId}
            vertical-position={this.verticalPosition}
            horizontal-position={this.horizontalPosition}
            relative-positioning
            onPopoverClosed={this.handlePopoverClose.bind(this)}
          >
            <div class="som-dpi-popover-content">
              <som-date-picker
                ref={ref => {
                  this.datePickerEl = ref;
                }}
                date={this.date} // Expects MM/DD/YYY format
                single-month-view={this.singleMonthView}
                onDateChange={this.handleDateChange.bind(this)}
              ></som-date-picker>
            </div>
          </som-popover>
        )}
      </Host>
    );
  }
}
